import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useConfirm, useAlert, usePrompt } from '../lib/ConfirmDialog';
import { haptics } from '../lib/haptics';
import {
  Plus,
  X,
  Trash2,
  ChefHat,
  ShoppingBag,
  Check,
  Calendar,
  ChevronDown,
  ChevronRight,
  Pencil,
  Users as UsersIcon,
  GripVertical,
  Share2,
  LogOut,
  Copy
} from 'lucide-react';
import { parseISO, isToday, isTomorrow, isPast, differenceInCalendarDays } from 'date-fns';
import { formatDate as fmt } from '../lib/dates';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/i18n';
import {
  createSharedShoppingList,
  renameSharedShoppingList,
  getShoppingListInviteUrl,
  deleteSharedShoppingList,
  leaveSharedShoppingList,
  listSharedShoppingListMembers
} from '../lib/sharedShoppingLists';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const UNIT_OPTIONS = [
  { value: '', label: '—' },
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'kg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'L' },
  { value: 'Stk', labelKey: 'shopping.unitPieces' },
  { value: 'Pkg', labelKey: 'shopping.unitPackages' }
];

// Avatar palette mirrored from Settings to ensure colors match.
const AVATAR_COLORS = [
  { id: 'blue', value: '#3b82f6' },
  { id: 'green', value: '#22c55e' },
  { id: 'red', value: '#ef4444' },
  { id: 'amber', value: '#f59e0b' },
  { id: 'purple', value: '#a855f7' },
  { id: 'pink', value: '#ec4899' },
  { id: 'teal', value: '#14b8a6' },
  { id: 'gray', value: '#6b7280' }
];

const formatDeadline = (isoDate, t) => {
  if (!isoDate) return null;
  const d = parseISO(isoDate);
  if (isToday(d)) return { label: t('common.today'), tone: 'urgent' };
  if (isTomorrow(d)) return { label: t('common.tomorrow'), tone: 'soon' };
  const diff = differenceInCalendarDays(d, new Date());
  if (isPast(d)) return { label: t('shopping.daysAgo', { n: Math.abs(diff) }), tone: 'past' };
  if (diff <= 3) return { label: t('shopping.inDays', { n: diff }), tone: 'soon' };
  return { label: fmt(d, 'short'), tone: 'normal' };
};

const ShoppingList = ({ data, updateData }) => {
  const confirm = useConfirm();
  const alert = useAlert();
  const prompt = usePrompt();
  const { user } = useAuth();
  const { t } = useTranslation();
  const shopping = data.shopping || { recipes: [], items: [], sharedLists: [] };
  const allRecipes = useMemo(() => shopping.recipes || [], [shopping.recipes]);
  const allItems = useMemo(() => shopping.items || [], [shopping.items]);
  const sharedLists = useMemo(() => shopping.sharedLists || [], [shopping.sharedLists]);

  // Active scope: null = personal, otherwise shared list UUID.
  const [activeListId, setActiveListId] = useState(() => {
    try {
      return localStorage.getItem('shopping_active_list') || null;
    } catch {
      return null;
    }
  });

  // Make sure we don't keep an activeListId pointing to a list the user is
  // no longer a member of (after leave/delete).
  useEffect(() => {
    if (activeListId && !sharedLists.some((l) => l.id === activeListId)) {
      setActiveListId(null);
    }
  }, [activeListId, sharedLists]);

  useEffect(() => {
    try {
      if (activeListId) localStorage.setItem('shopping_active_list', activeListId);
      else localStorage.removeItem('shopping_active_list');
    } catch { /* ignore */ }
  }, [activeListId]);

  // Filter to current scope. sharedListId === null/undefined means personal.
  const recipes = useMemo(
    () => allRecipes.filter((r) => (r.sharedListId || null) === activeListId),
    [allRecipes, activeListId]
  );
  const items = useMemo(
    () => allItems.filter((it) => (it.sharedListId || null) === activeListId),
    [allItems, activeListId]
  );

  const activeList = useMemo(
    () => sharedLists.find((l) => l.id === activeListId) || null,
    [sharedLists, activeListId]
  );

  // Members of the current shared list (loaded async).
  const [members, setMembers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!activeListId) {
      setMembers([]);
      return undefined;
    }
    listSharedShoppingListMembers(activeListId)
      .then((rows) => {
        if (!cancelled) setMembers(rows || []);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load list members', err);
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeListId]);

  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  const [newRecipe, setNewRecipe] = useState({ name: '', neededBy: '' });
  const [quickAdd, setQuickAdd] = useState({ recipeId: null, name: '', amount: '', unit: 'g' });
  const [collapsedSections, setCollapsedSections] = useState({});
  const [purchasedCollapsed, setPurchasedCollapsed] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [showManageList, setShowManageList] = useState(false);
  const [newListName, setNewListName] = useState('');

  // ----- helpers to commit a change to the global recipes/items arrays -----
  const commit = (patch) => {
    updateData({
      shopping: {
        ...shopping,
        recipes: allRecipes,
        items: allItems,
        sharedLists,
        ...patch
      }
    });
  };

  const replaceScopedRecipes = (nextScoped) => {
    const others = allRecipes.filter((r) => (r.sharedListId || null) !== activeListId);
    commit({ recipes: [...others, ...nextScoped] });
  };
  const replaceScopedItems = (nextScoped) => {
    const others = allItems.filter((it) => (it.sharedListId || null) !== activeListId);
    commit({ items: [...others, ...nextScoped] });
  };

  // ---------- Recipe (Group) CRUD ----------
  const addRecipe = () => {
    if (!newRecipe.name.trim()) return;
    const recipe = {
      id: Date.now().toString(),
      name: newRecipe.name.trim(),
      neededBy: newRecipe.neededBy || null,
      createdAt: new Date().toISOString(),
      sharedListId: activeListId,
      position: recipes.length
    };
    replaceScopedRecipes([...recipes, recipe]);
    setNewRecipe({ name: '', neededBy: '' });
    setShowAddRecipe(false);
  };

  const closeAddRecipe = () => {
    setShowAddRecipe(false);
    setNewRecipe({ name: '', neededBy: '' });
  };
  const openAddRecipe = () => {
    setNewRecipe({ name: '', neededBy: '' });
    setShowAddRecipe(true);
  };

  const updateRecipe = (id, patch) => {
    replaceScopedRecipes(recipes.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const deleteRecipe = async (id) => {
    if (!(await confirm({ message: t('shopping.deleteGroupConfirm'), danger: true }))) return;
    const otherRecipes = allRecipes.filter((r) => !((r.sharedListId || null) === activeListId && r.id === id));
    const otherItems = allItems.filter(
      (it) => !((it.sharedListId || null) === activeListId && it.recipeId === id)
    );
    commit({ recipes: otherRecipes, items: otherItems });
  };

  // ---------- Item CRUD ----------
  const addItem = (recipeId) => {
    const name = quickAdd.name.trim();
    if (!name || quickAdd.recipeId !== recipeId) return;
    const inGroup = items.filter((it) => (it.recipeId || null) === (recipeId || null) && !it.checked);
    const item = {
      id: Date.now().toString(),
      name,
      amount: quickAdd.amount.trim(),
      unit: quickAdd.unit,
      recipeId: recipeId || null,
      checked: false,
      sharedListId: activeListId,
      position: inGroup.length,
      createdAt: new Date().toISOString()
    };
    replaceScopedItems([...items, item]);
    setQuickAdd({ recipeId, name: '', amount: '', unit: quickAdd.unit });
  };

  const toggleItem = (id) => {
    haptics.tap();
    replaceScopedItems(
      items.map((it) =>
        it.id === id
          ? { ...it, checked: !it.checked, checkedAt: !it.checked ? Date.now() : null }
          : it
      )
    );
  };

  const deleteItem = (id) => {
    replaceScopedItems(items.filter((it) => it.id !== id));
  };

  // ---------- Auto-purge (24h checked items / 5d empty groups) ----------
  const stateRef = useRef({ recipes: allRecipes, items: allItems, sharedLists });
  stateRef.current = { recipes: allRecipes, items: allItems, sharedLists };
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const itemCutoff = now - 24 * 60 * 60 * 1000;
      const recipeCutoff = now - 5 * 24 * 60 * 60 * 1000;
      const cur = stateRef.current;

      const nextItems = cur.items.filter((it) => {
        if (!it.checked) return true;
        const ts = it.checkedAt ? Number(it.checkedAt) : NaN;
        if (!Number.isFinite(ts)) return true;
        return ts >= itemCutoff;
      });

      const itemsByRecipe = nextItems.reduce((acc, it) => {
        if (it.recipeId) acc[it.recipeId] = (acc[it.recipeId] || 0) + 1;
        return acc;
      }, {});
      const nextRecipes = [];
      for (const r of cur.recipes) {
        const count = itemsByRecipe[r.id] || 0;
        if (count > 0) {
          if (r.emptySince) nextRecipes.push({ ...r, emptySince: null });
          else nextRecipes.push(r);
          continue;
        }
        const sinceTs = r.emptySince
          ? Date.parse(r.emptySince)
          : r.createdAt
          ? Date.parse(r.createdAt)
          : NaN;
        if (Number.isFinite(sinceTs) && sinceTs < recipeCutoff) continue;
        if (!r.emptySince) {
          nextRecipes.push({ ...r, emptySince: new Date().toISOString() });
        } else {
          nextRecipes.push(r);
        }
      }

      const itemsChanged =
        nextItems.length !== cur.items.length ||
        nextItems.some((it, i) => it !== cur.items[i]);
      const recipesChanged =
        nextRecipes.length !== cur.recipes.length ||
        nextRecipes.some((r, i) => r !== cur.recipes[i]);
      if (itemsChanged || recipesChanged) {
        updateData({
          shopping: {
            ...stateRef.current,
            recipes: nextRecipes,
            items: nextItems,
            sharedLists: stateRef.current.sharedLists
          }
        });
      }
    };
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Drag and drop ----------
  // Each draggable item id: "item:<id>". Each droppable container: "group:<recipeId|null>".
  // Items within a group are kept ordered by `position` (renumbered on drop).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortPos = (a, b) => {
    const ap = Number.isFinite(a.position) ? a.position : 0;
    const bp = Number.isFinite(b.position) ? b.position : 0;
    return ap - bp;
  };

  // Grouped active items
  const { activeByRecipe, activeExtras, purchasedByRecipe, purchasedExtras } = useMemo(() => {
    const abr = {};
    const pbr = {};
    const ae = [];
    const pe = [];
    for (const r of recipes) {
      abr[r.id] = [];
      pbr[r.id] = [];
    }
    for (const it of items) {
      if (it.checked) {
        if (it.recipeId && pbr[it.recipeId]) pbr[it.recipeId].push(it);
        else pe.push(it);
      } else if (it.recipeId && abr[it.recipeId]) {
        abr[it.recipeId].push(it);
      } else {
        ae.push(it);
      }
    }
    for (const k of Object.keys(abr)) abr[k].sort(sortPos);
    for (const k of Object.keys(pbr)) pbr[k].sort(sortPos);
    ae.sort(sortPos);
    pe.sort(sortPos);
    return { activeByRecipe: abr, activeExtras: ae, purchasedByRecipe: pbr, purchasedExtras: pe };
  }, [items, recipes]);

  const sortedRecipes = useMemo(() => {
    return [...recipes].sort((a, b) => {
      const ap = Number.isFinite(a.position) ? a.position : 1e9;
      const bp = Number.isFinite(b.position) ? b.position : 1e9;
      if (ap !== bp) return ap - bp;
      if (a.neededBy && !b.neededBy) return -1;
      if (!a.neededBy && b.neededBy) return 1;
      if (a.neededBy && b.neededBy) return a.neededBy.localeCompare(b.neededBy);
      return (a.createdAt || '').localeCompare(b.createdAt || '');
    });
  }, [recipes]);

  const totalPurchased =
    purchasedExtras.length + recipes.reduce((s, r) => s + (purchasedByRecipe[r.id]?.length || 0), 0);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id).replace(/^item:/, '');
    const overIdRaw = String(over.id);

    const activeItem = items.find((it) => it.id === activeId);
    if (!activeItem) return;

    let targetRecipeId;
    let targetIndex;
    if (overIdRaw.startsWith('group:')) {
      targetRecipeId = overIdRaw.slice(6) === 'null' ? null : overIdRaw.slice(6);
      const list =
        targetRecipeId === null
          ? activeExtras
          : activeByRecipe[targetRecipeId] || [];
      targetIndex = list.length;
    } else {
      const overItemId = overIdRaw.replace(/^item:/, '');
      const overItem = items.find((it) => it.id === overItemId);
      if (!overItem) return;
      targetRecipeId = overItem.recipeId || null;
      const list =
        targetRecipeId === null
          ? activeExtras
          : activeByRecipe[targetRecipeId] || [];
      targetIndex = list.findIndex((it) => it.id === overItemId);
      if (targetIndex < 0) targetIndex = list.length;
    }

    // Reorder within the same group → arrayMove.
    const sourceRecipeId = activeItem.recipeId || null;
    let nextActive;
    if (sourceRecipeId === targetRecipeId) {
      const list =
        targetRecipeId === null
          ? activeExtras
          : activeByRecipe[targetRecipeId] || [];
      const fromIdx = list.findIndex((it) => it.id === activeId);
      if (fromIdx === -1) return;
      const moved = arrayMove(list, fromIdx, targetIndex).map((it, i) => ({
        ...it,
        position: i
      }));
      nextActive = moved;
    } else {
      // Move into a different group.
      const targetList =
        targetRecipeId === null
          ? [...activeExtras]
          : [...(activeByRecipe[targetRecipeId] || [])];
      const moved = {
        ...activeItem,
        recipeId: targetRecipeId,
        position: targetIndex
      };
      targetList.splice(targetIndex, 0, moved);
      nextActive = targetList.map((it, i) => ({ ...it, position: i }));
    }

    // Build new items array preserving everything else (purchased/other groups).
    const replacedIds = new Set(nextActive.map((it) => it.id));
    const sourceList =
      sourceRecipeId === targetRecipeId
        ? []
        : (sourceRecipeId === null
            ? activeExtras
            : activeByRecipe[sourceRecipeId] || []
          )
            .filter((it) => it.id !== activeId)
            .map((it, i) => ({ ...it, position: i }));
    sourceList.forEach((it) => replacedIds.add(it.id));

    const nextItems = items.map((it) => {
      if (replacedIds.has(it.id)) {
        const updated =
          nextActive.find((x) => x.id === it.id) ||
          sourceList.find((x) => x.id === it.id);
        return updated || it;
      }
      return it;
    });

    replaceScopedItems(nextItems);
  };

  // ---------- Shared list management ----------
  const handleCreateSharedList = async () => {
    const name = newListName.trim();
    if (!name) return;
    try {
      const created = await createSharedShoppingList(name);
      // Also add to local sharedLists so UI updates without full reload.
      const next = [
        ...sharedLists,
        { id: created.id, name: created.name, ownerId: user?.id, isOwner: true, createdAt: new Date().toISOString() }
      ];
      commit({ sharedLists: next });
      setActiveListId(created.id);
      setShowCreateList(false);
      setNewListName('');
      // Try to copy invite link.
      try {
        const url = await getShoppingListInviteUrl(created.id);
        if (navigator.clipboard) await navigator.clipboard.writeText(url);
        await alert({
          message: t('shopping.listCreatedCopied', { name, url })
        }).catch(() => null);
      } catch { /* ignore */ }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Create shared list failed', err);
      window.alert(t('shopping.createListFailed', { message: err.message || err }));
    }
  };

  const handleCopyInvite = async () => {
    if (!activeListId) return;
    try {
      const url = await getShoppingListInviteUrl(activeListId);
      if (navigator.clipboard) await navigator.clipboard.writeText(url);
      await alert({ message: t('shopping.inviteCopied', { url }) }).catch(() => null);
    } catch (err) {
      window.alert(t('shopping.inviteFailed', { message: err.message || err }));
    }
  };

  const handleRenameList = async () => {
    if (!activeList || !activeList.isOwner) return;
    const next = await prompt({
      title: t('common.renameList'),
      placeholder: t('common.newName'),
      defaultValue: activeList.name,
      confirmText: t('common.save')
    });
    if (!next || next.trim() === activeList.name) return;
    try {
      await renameSharedShoppingList(activeListId, next.trim());
      const nextLists = sharedLists.map((l) =>
        l.id === activeListId ? { ...l, name: next.trim() } : l
      );
      commit({ sharedLists: nextLists });
    } catch (err) {
      window.alert(t('shopping.renameFailed', { message: err.message || err }));
    }
  };

  const handleLeaveList = async () => {
    if (!activeList) return;
    const ok = await confirm({
      message: t('shopping.leaveListConfirm', { name: activeList.name }),
      danger: true
    });
    if (!ok) return;
    try {
      await leaveSharedShoppingList(activeListId);
      commit({ sharedLists: sharedLists.filter((l) => l.id !== activeListId) });
      setActiveListId(null);
      setShowManageList(false);
    } catch (err) {
      window.alert(t('shopping.leaveFailed', { message: err.message || err }));
    }
  };

  const handleDeleteList = async () => {
    if (!activeList || !activeList.isOwner) return;
    const ok = await confirm({
      message: t('shopping.deleteListConfirm', { name: activeList.name }),
      danger: true
    });
    if (!ok) return;
    try {
      await deleteSharedShoppingList(activeListId);
      commit({ sharedLists: sharedLists.filter((l) => l.id !== activeListId) });
      setActiveListId(null);
      setShowManageList(false);
    } catch (err) {
      window.alert(t('shopping.deleteFailed', { message: err.message || err }));
    }
  };

  // ---------- Render ----------
  return (
    <div className="space-y-4 max-w-3xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {activeList ? activeList.name : t('nav.shopping')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            {activeList ? t('shopping.sharedList') : t('shopping.personalList')}
          </p>
        </div>
        <button
          onClick={openAddRecipe}
          className="btn bg-primary-600 text-white hover:bg-primary-700 flex items-center gap-2 !px-3 !py-2 sm:!px-4 rounded-lg text-sm font-medium"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">{t('shopping.group')}</span>
        </button>
      </div>

      {/* List selector pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        <ListPill
          active={activeListId === null}
          onClick={() => setActiveListId(null)}
          icon={<ShoppingBag size={14} />}
          label={t('nav.personal')}
        />
        {sharedLists.map((l) => (
          <ListPill
            key={l.id}
            active={activeListId === l.id}
            onClick={() => setActiveListId(l.id)}
            icon={<UsersIcon size={14} />}
            label={l.name}
          />
        ))}
        <button
          onClick={() => {
            setNewListName('');
            setShowCreateList(true);
          }}
          className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-gray-300 dark:border-gray-700 text-xs font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/40"
        >
          <Plus size={14} /> {t('shopping.sharedList')}
        </button>
      </div>

      {/* Members + manage row (shared lists only) */}
      {activeList && (
        <div className="flex items-center justify-between gap-3 px-1">
          <div className="flex items-center -space-x-2">
            {members.slice(0, 6).map((m) => (
              <Avatar key={m.userId} member={m} size={28} />
            ))}
            {members.length > 6 && (
              <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 text-xs flex items-center justify-center text-gray-700 dark:text-gray-200 ring-2 ring-white dark:ring-gray-900">
                +{members.length - 6}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleCopyInvite}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
              title={t('shopping.copyInvite')}
            >
              <Share2 size={16} />
            </button>
            <button
              onClick={() => setShowManageList(true)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
              title={t('shopping.manageList')}
            >
              <Pencil size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Active list (DnD context wraps everything draggable) */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-4">
          {sortedRecipes.length === 0 && activeExtras.length === 0 && totalPurchased === 0 && (
            <div className="text-center py-16 text-gray-500">
              <ChefHat size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="font-medium">{t('common.noEntriesYet')}</p>
              <p className="text-sm mt-1">{t('shopping.emptyHint')}</p>
            </div>
          )}

          {sortedRecipes.map((recipe) => (
            <RecipeSection
              key={recipe.id}
              recipe={recipe}
              items={activeByRecipe[recipe.id] || []}
              collapsed={!!collapsedSections[recipe.id]}
              onToggleCollapse={() =>
                setCollapsedSections({
                  ...collapsedSections,
                  [recipe.id]: !collapsedSections[recipe.id]
                })
              }
              onToggleItem={toggleItem}
              onDeleteItem={deleteItem}
              onDeleteRecipe={() => deleteRecipe(recipe.id)}
              onEditRecipe={() => setEditingRecipeId(recipe.id)}
              isEditing={editingRecipeId === recipe.id}
              onSaveRecipe={(patch) => {
                updateRecipe(recipe.id, patch);
                setEditingRecipeId(null);
              }}
              onCancelEdit={() => setEditingRecipeId(null)}
              quickAdd={quickAdd}
              setQuickAdd={setQuickAdd}
              onAddItem={() => addItem(recipe.id)}
            />
          ))}

          <ExtrasSection
            items={activeExtras}
            onToggleItem={toggleItem}
            onDeleteItem={deleteItem}
            quickAdd={quickAdd}
            setQuickAdd={setQuickAdd}
            onAddItem={() => addItem(null)}
          />
        </div>
      </DndContext>

      {/* Purchased */}
      {totalPurchased > 0 && (
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={() => setPurchasedCollapsed(!purchasedCollapsed)}
            className="w-full flex items-center justify-between mb-3 px-1"
          >
            <div className="flex items-center gap-2">
              <ShoppingBag size={18} className="text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-300">{t('common.completed')}</h2>
              <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2 py-0.5">
                {totalPurchased}
              </span>
            </div>
            {purchasedCollapsed ? (
              <ChevronRight size={18} className="text-gray-400" />
            ) : (
              <ChevronDown size={18} className="text-gray-400" />
            )}
          </button>

          {!purchasedCollapsed && (
            <div className="space-y-4 opacity-70">
              {sortedRecipes
                .filter((r) => (purchasedByRecipe[r.id] || []).length > 0)
                .map((recipe) => (
                  <div key={recipe.id}>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                      {recipe.name}
                    </h3>
                    <div className="space-y-1">
                      {purchasedByRecipe[recipe.id].map((it) => (
                        <ItemRow
                          key={it.id}
                          item={it}
                          onToggle={() => toggleItem(it.id)}
                          onDelete={() => deleteItem(it.id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              {purchasedExtras.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                    {t('shopping.otherPurchases')}
                  </h3>
                  <div className="space-y-1">
                    {purchasedExtras.map((it) => (
                      <ItemRow
                        key={it.id}
                        item={it}
                        onToggle={() => toggleItem(it.id)}
                        onDelete={() => deleteItem(it.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Group Modal */}
      {showAddRecipe && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeAddRecipe}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold dark:text-gray-100">{t('todo.newGroup')}</h3>
              <button onClick={closeAddRecipe} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X size={20} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addRecipe();
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('shopping.groupName')}
                </label>
                <input
                  type="text"
                  autoFocus
                  className="input"
                  placeholder={t('shopping.groupNamePlaceholder')}
                  value={newRecipe.name}
                  onChange={(e) => setNewRecipe({ ...newRecipe, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('shopping.neededBy')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
                </label>
                <input
                  type="date"
                  className="input"
                  value={newRecipe.neededBy}
                  onChange={(e) => setNewRecipe({ ...newRecipe, neededBy: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={!newRecipe.name.trim()}
                  className="btn bg-primary-600 text-white hover:bg-primary-700 flex-1 disabled:opacity-50"
                >
                  {t('common.add')}
                </button>
                <button type="button" onClick={closeAddRecipe} className="btn-secondary">
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Shared List Modal */}
      {showCreateList && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateList(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold dark:text-gray-100">{t('shopping.newSharedList')}</h3>
              <button
                onClick={() => setShowCreateList(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateSharedList();
              }}
              className="space-y-4"
            >
              <input
                type="text"
                autoFocus
                className="input"
                placeholder={t('shopping.listNamePlaceholder')}
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                {t('shopping.createListHint')}
              </p>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={!newListName.trim()}
                  className="btn bg-primary-600 text-white hover:bg-primary-700 flex-1 disabled:opacity-50"
                >
                  {t('sharedCalendar.create')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateList(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Shared List Modal */}
      {showManageList && activeList && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowManageList(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold dark:text-gray-100">{activeList.name}</h3>
              <button
                onClick={() => setShowManageList(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">{t('sharedCalendar.membersTitle')}</p>
                <div className="space-y-2">
                  {members.map((m) => (
                    <div key={m.userId} className="flex items-center gap-3">
                      <Avatar member={m} size={32} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm dark:text-gray-100 truncate">
                          {m.displayName || m.email || t('common.unknown')}
                        </div>
                        {m.email && (
                          <div className="text-xs text-gray-500 truncate">{m.email}</div>
                        )}
                      </div>
                      {m.isOwner && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                          {t('sharedCalendar.owner')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-1">
                <button
                  onClick={handleCopyInvite}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-sm"
                >
                  <Copy size={16} className="text-gray-500" />
                  <span>{t('shopping.copyInvite')}</span>
                </button>
                {activeList.isOwner && (
                  <button
                    onClick={handleRenameList}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-sm"
                  >
                    <Pencil size={16} className="text-gray-500" />
                    <span>{t('common.renameList')}</span>
                  </button>
                )}
                {!activeList.isOwner && (
                  <button
                    onClick={handleLeaveList}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm text-red-600"
                  >
                    <LogOut size={16} />
                    <span>{t('common.leaveList')}</span>
                  </button>
                )}
                {activeList.isOwner && (
                  <button
                    onClick={handleDeleteList}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm text-red-600"
                  >
                    <Trash2 size={16} />
                    <span>{t('common.deleteList')}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Sub-components ----------------

const ListPill = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
      active
        ? 'bg-primary-600 text-white'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
    }`}
  >
    {icon}
    <span className="truncate max-w-[10rem]">{label}</span>
  </button>
);

const Avatar = ({ member, size = 28 }) => {
  const colorObj =
    AVATAR_COLORS.find((c) => c.id === member?.avatarColor) || AVATAR_COLORS[0];
  const initial = (member?.displayName || member?.email || '?').trim().charAt(0).toUpperCase();
  const usePhoto = member?.avatarKind === 'photo' && member?.avatarUrl;
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold ring-2 ring-white dark:ring-gray-900 overflow-hidden"
      style={{
        width: size,
        height: size,
        backgroundColor: usePhoto ? '#e5e7eb' : colorObj.value,
        fontSize: Math.round(size * 0.42)
      }}
      title={member?.displayName || member?.email || ''}
    >
      {usePhoto ? (
        <img
          src={member.avatarUrl}
          alt=""
          className="w-full h-full object-cover"
        />
      ) : member?.avatarEmoji ? (
        <span style={{ fontSize: Math.round(size * 0.55) }}>{member.avatarEmoji}</span>
      ) : (
        initial
      )}
    </div>
  );
};

const RecipeSection = ({
  recipe,
  items,
  collapsed,
  onToggleCollapse,
  onToggleItem,
  onDeleteItem,
  onDeleteRecipe,
  onEditRecipe,
  isEditing,
  onSaveRecipe,
  onCancelEdit,
  quickAdd,
  setQuickAdd,
  onAddItem
}) => {
  const { t } = useTranslation();
  const [editName, setEditName] = useState(recipe.name);
  const [editDate, setEditDate] = useState(recipe.neededBy || '');

  useEffect(() => {
    if (isEditing) {
      setEditName(recipe.name);
      setEditDate(recipe.neededBy || '');
    }
  }, [isEditing, recipe]);

  const deadline = formatDeadline(recipe.neededBy, t);
  const deadlineClass =
    deadline?.tone === 'urgent'
      ? 'bg-red-100 text-red-700'
      : deadline?.tone === 'soon'
      ? 'bg-orange-100 text-orange-700'
      : deadline?.tone === 'past'
      ? 'bg-gray-200 text-gray-600'
      : 'bg-blue-100 text-blue-700';

  const itemIds = items.map((it) => `item:${it.id}`);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-800">
        <button
          onClick={onToggleCollapse}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {collapsed ? (
            <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
          )}
          <ChefHat size={16} className="text-gray-500 flex-shrink-0" />
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm min-w-0"
              />
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm"
              />
            </div>
          ) : (
            <>
              <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                {recipe.name}
              </span>
              {deadline && (
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${deadlineClass}`}
                >
                  <Calendar size={10} />
                  {deadline.label}
                </span>
              )}
              {items.length > 0 && (
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {items.length}
                </span>
              )}
            </>
          )}
        </button>

        <div className="flex items-center gap-1 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={() =>
                  onSaveRecipe({
                    name: editName.trim() || recipe.name,
                    neededBy: editDate || null
                  })
                }
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-primary-600"
              >
                <Check size={16} />
              </button>
              <button
                onClick={onCancelEdit}
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEditRecipe}
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500"
                title={t('common.edit')}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={onDeleteRecipe}
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-red-500"
                title={t('shopping.deleteGroup')}
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="p-2">
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <DroppableGroup id={`group:${recipe.id}`} empty={items.length === 0}>
              {items.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">
                  {t('shopping.emptyGroupHint')}
                </p>
              ) : (
                <div className="space-y-0.5">
                  {items.map((it) => (
                    <SortableItem
                      key={it.id}
                      item={it}
                      onToggle={() => onToggleItem(it.id)}
                      onDelete={() => onDeleteItem(it.id)}
                    />
                  ))}
                </div>
              )}
            </DroppableGroup>
          </SortableContext>

          <QuickAddRow
            active={quickAdd.recipeId === recipe.id}
            quickAdd={quickAdd}
            setQuickAdd={setQuickAdd}
            recipeId={recipe.id}
            onAddItem={onAddItem}
          />
        </div>
      )}
    </div>
  );
};

const ExtrasSection = ({ items, onToggleItem, onDeleteItem, quickAdd, setQuickAdd, onAddItem }) => {
  const { t } = useTranslation();
  const itemIds = items.map((it) => `item:${it.id}`);
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-800">
        <ShoppingBag size={16} className="text-gray-500" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">{t('shopping.extras')}</span>
        {items.length > 0 && (
          <span className="text-xs text-gray-400 ml-auto">{items.length}</span>
        )}
      </div>
      <div className="p-2">
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <DroppableGroup id="group:null" empty={items.length === 0}>
            {items.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">
                {t('shopping.extrasHint')}
              </p>
            ) : (
              <div className="space-y-0.5">
                {items.map((it) => (
                  <SortableItem
                    key={it.id}
                    item={it}
                    onToggle={() => onToggleItem(it.id)}
                    onDelete={() => onDeleteItem(it.id)}
                  />
                ))}
              </div>
            )}
          </DroppableGroup>
        </SortableContext>
        <QuickAddRow
          active={quickAdd.recipeId === null}
          quickAdd={quickAdd}
          setQuickAdd={setQuickAdd}
          recipeId={null}
          onAddItem={onAddItem}
        />
      </div>
    </div>
  );
};

// Empty groups need their own drop target since SortableContext with no
// sortable items isn't a drop zone on its own.
const DroppableGroup = ({ id, children, empty }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[2.5rem] rounded-md transition-colors ${
        empty ? 'border border-dashed border-gray-200 dark:border-gray-800' : ''
      } ${isOver ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
    >
      {children}
    </div>
  );
};

const SortableItem = ({ item, onToggle, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `item:${item.id}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  return (
    <div ref={setNodeRef} style={style}>
      <ItemRow
        item={item}
        onToggle={onToggle}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

const ItemRow = ({ item, onToggle, onDelete, dragHandleProps }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 group ${
        item.checked ? 'text-gray-400' : ''
      }`}
    >
      {dragHandleProps && (
        <button
          {...dragHandleProps}
          className="touch-none cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 flex-shrink-0"
          aria-label={t('shopping.dragHandle')}
        >
          <GripVertical size={16} />
        </button>
      )}
      <button
        onClick={onToggle}
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          item.checked
            ? 'bg-primary-600 border-primary-600 text-white'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
        }`}
      >
        {item.checked && <Check size={12} strokeWidth={3} />}
      </button>
      <span
        className={`flex-1 text-sm min-w-0 truncate ${
          item.checked ? 'line-through' : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {item.name}
      </span>
      {item.amount && (
        <span
          className={`text-xs flex-shrink-0 ${
            item.checked ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {item.amount}
          {item.unit ? ` ${item.unit}` : ''}
        </span>
      )}
      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-red-400 transition-opacity flex-shrink-0"
        title={t('common.delete')}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const QuickAddRow = ({ active, quickAdd, setQuickAdd, recipeId, onAddItem }) => {
  const { t } = useTranslation();
  const nameRef = useRef(null);

  const handleFocus = () => {
    if (!active) setQuickAdd({ recipeId, name: '', amount: '', unit: '' });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onAddItem();
        setTimeout(() => nameRef.current?.focus(), 0);
      }}
      className="flex items-center gap-2 px-3 py-2 mt-1 border-t border-dashed border-gray-200 dark:border-gray-800"
    >
      <Plus size={16} className="text-gray-400 flex-shrink-0" />
      <input
        ref={nameRef}
        type="text"
        placeholder={t('shopping.addItemPlaceholder')}
        value={active ? quickAdd.name : ''}
        onFocus={handleFocus}
        onChange={(e) => setQuickAdd({ ...quickAdd, recipeId, name: e.target.value })}
        className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-gray-400 min-w-0"
      />
      {active && (
        <>
          <input
            type="text"
            placeholder={t('shopping.amount')}
            value={quickAdd.amount}
            onChange={(e) => setQuickAdd({ ...quickAdd, amount: e.target.value })}
            className="w-16 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs text-right"
          />
          <select
            value={quickAdd.unit}
            onChange={(e) => setQuickAdd({ ...quickAdd, unit: e.target.value })}
            disabled={!quickAdd.amount.trim()}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1 py-1 text-xs disabled:opacity-40"
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.labelKey ? t(u.labelKey) : u.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={!quickAdd.name.trim()}
            className="px-2 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-40"
          >
            {t('common.add')}
          </button>
        </>
      )}
    </form>
  );
};

export default ShoppingList;
